import classnames from "classnames";
import { inject } from "mobx-react";
import { Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { Store } from "@inferno/renderer-shared-core";
import type { AppEnvironment } from "@inferno/renderer-shared-core";
import { SocialIcons } from "@inferno/renderer-shared-ui";
import type { ContactFragment, SocialFragment } from "@ihr-radioedit/inferno-webapi";
import { formatImage } from "@ihr-radioedit/inferno-core";
import { socialConfigAbstract } from "@inferno/renderer-shared-core";
import { Container, MagicLink } from "../../ui";
import { ADVERTISING_PHONE_NUMBER } from "../../ui/constants";
import type { PageBlockInterface } from "../Block.component";
import { ContactForm } from "./ContactForm.component";
import "./Contact.style.scss";
import i18n from "i18next";

interface ContactProps {
  store?: Store;
}

const Social = ({
  socialSwitch,
  social,
}: {
  socialSwitch: boolean | null | undefined;
  social?: SocialFragment | null;
}) => {
  const { t } = useTranslation();
  if (!socialSwitch || !social) {
    return null;
  }
  return (
    <section title="social">
      <h2>{t("follow_us")}</h2>
      <SocialIcons networks={socialConfigAbstract(social)} />
    </section>
  );
};

const PhoneDisplay = ({
  title,
  phoneNumber,
  list = false,
}: {
  title: string;
  phoneNumber: string | null | undefined;
  list?: boolean;
}) => {
  if (!phoneNumber) {
    return null;
  }
  if (list) {
    return (
      <li>
        <span className="phone-details">
          {title}: <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
        </span>
      </li>
    );
  }
  return (
    <span className="phone-details">
      {title}: <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
    </span>
  );
};

const Phones = ({ contact }: { contact: ContactFragment | null | undefined }) => {
  const { t } = useTranslation();
  if (!contact?.phone_text_numbers_switch) {
    return null;
  }
  return (
    <Fragment>
      <section title="Call Us">
        <h2>{t("call_us")}</h2>
        <ul className="phone-list">
          <PhoneDisplay list={true} title={t("studio_line")} phoneNumber={contact.request_phone_number} />
          <PhoneDisplay list={true} title={t("traffic_tipline")} phoneNumber={contact.traffic_tip_phone} />
          <PhoneDisplay list={true} title={t("business_line")} phoneNumber={contact.business_phone_number} />
          <PhoneDisplay list={true} title={t("newsroom_line")} phoneNumber={contact.newsroom_phone} />
          <PhoneDisplay list={true} title={t("advertise_us")} phoneNumber={ADVERTISING_PHONE_NUMBER} />
        </ul>
      </section>
      {contact.sms_number ? (
        <section title="Text Us">
          <h2>{t("text_us")}</h2>
          <PhoneDisplay title={t("studio_line")} phoneNumber={contact.sms_number} />
          <p>{t("disclaimer")}</p>
        </section>
      ) : null}
    </Fragment>
  );
};

const Advertise = ({ name }: { name: string }) => {
  const { t } = useTranslation();
  const translatedAdvertiseText = t("adv_learn", {
    link: `<a href="/advertise/">${t("send us an inquiry")}</a>`,
    number: `<a href="tel:${ADVERTISING_PHONE_NUMBER}">${ADVERTISING_PHONE_NUMBER}</a>`,
    name,
  });
  return (
    <section title="advertise">
      <h2>{t("advertising")}</h2>
      <div>
        <span dangerouslySetInnerHTML={{ __html: translatedAdvertiseText }} />
      </div>
    </section>
  );
};

const Support = ({
  showSupport,
  iheartHelpSite,
}: {
  showSupport: boolean | null | undefined;
  iheartHelpSite: string | undefined;
}) => {
  const { t } = useTranslation();
  if (!showSupport) {
    return null;
  }
  const translatedSupportText = t("support", {
    link: `<a href="${iheartHelpSite}" target="_blank" rel="noopener">help.iheart.com</a>`,
  });
  return (
    <section title="support">
      <h2>{t("ihr_support")}</h2>
      <p>
        <span dangerouslySetInnerHTML={{ __html: translatedSupportText }} />
      </p>
    </section>
  );
};

const ContestPromotionsSupport = ({
  contestPromotionSwitch,
  name,
  iheartHelpSiteContest,
}: {
  contestPromotionSwitch: boolean | null | undefined;
  name: string;
  iheartHelpSiteContest: string | undefined;
}) => {
  const { t } = useTranslation();

  if (!contestPromotionSwitch) {
    return null;
  }

  const translatedContestSupportText = i18n.t("contest_support", {
    stationName: name,
    link: `<a href="${iheartHelpSiteContest}" target="_blank" rel="noopener">help.iheart.com</a>`,
  });
  return (
    <section title="Contest Promotions Support">
      <h2>{t("contest_promotions_support_title")}</h2>
      <p>
        <span dangerouslySetInnerHTML={{ __html: translatedContestSupportText }} />
      </p>
    </section>
  );
};
const Address = ({
  contact,
  name,
  boldName = false,
  heading = "",
}: {
  contact: ContactFragment | null | undefined;
  name: string;
  boldName?: boolean;
  heading?: string;
}) => {
  if (!contact) {
    return null;
  }
  const nameClasses = classnames({
    "bold-text": boldName,
  });

  return (
    <div className="component-address">
      {heading}
      <address>
        {name ? <span className={nameClasses}>{name}</span> : null}
        {contact.location_address1 ? <span>{contact.location_address1}</span> : null}
        {contact.location_address2 ? <span>{contact.location_address2}</span> : null}
        <span>
          {contact.location_city}, {contact.location_state} {contact.location_zip}
        </span>
      </address>
    </div>
  );
};

const PrizePickup = ({ contact, name }: { contact: ContactFragment | null | undefined; name: string }) => {
  const { t } = useTranslation();
  if (!contact) {
    return null;
  }
  const mapQuery = `${contact.location_address1},${contact.location_city}+${contact.location_state}+${contact.location_zip}`;
  if (!contact.prize_pickup_directions_switch) {
    return null;
  }
  return (
    <section title="prize pickup">
      <h2>{t("prize").replace("&amp;", "&")}</h2>
      <Address contact={contact} name={name} boldName={true} />
      {contact.office_hours ? (
        <p>
          {t("pickup")}: {contact.office_hours}
        </p>
      ) : null}
      <div className="contact-map embed-responsive embed-responsive-16by9">
        <iframe
          title={t("map_title", { name })}
          src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyDBLu1y4qtqN1TpwjtFDiGJqVUxyUrK0EI&q=${mapQuery}&zoom=11`}
          frameBorder="0"
          marginWidth={0}
          marginHeight={0}
          scrolling="no"
          className="embed-responsive-item"
        />
      </div>
      {contact.prize_pickup_info ? <p>{contact.prize_pickup_info}</p> : null}
    </section>
  );
};

const EmploymentLink = ({ contact }: { contact: ContactFragment | null | undefined }) => {
  const { t } = useTranslation();
  if (!contact) {
    return null;
  }
  if (contact.employment_link_override) {
    return (
      <p>
        {t("openings")}
        <MagicLink to={contact.employment_link_override} target="_blank">
          {t("click")}
        </MagicLink>
      </p>
    );
  }

  return (
    <p>
      {t("openings")}
      <MagicLink to="//www.iheartmediacareers.com/" target="_blank">
        {t("jobs")}
      </MagicLink>
    </p>
  );
};

const EEOReportAsset = ({ eeoReportAsset }: { eeoReportAsset: string }) => {
  const { t } = useTranslation();
  if (!eeoReportAsset) {
    return null;
  }
  return (
    <p>
      {t("eeo")}
      <MagicLink to={`${eeoReportAsset}?passthrough=1`} target="_blank">
        {t("here")}
      </MagicLink>
      {"."}
    </p>
  );
};

const EmploymentInfo = ({
  contact,
  name,
  env,
}: {
  contact: ContactFragment | null | undefined;
  name: string;
  env: AppEnvironment;
}) => {
  const { t } = useTranslation();
  if (!contact || !contact.employment_info_switch) {
    return null;
  }
  return (
    <section title="employment">
      <h2>{t("employment")}</h2>
      <h3>{t("recrut_alert")}</h3>
      <p>{t(`recruitment`)}</p>
      <Address name={name} contact={contact} heading={t("fcc_contact")} />
      {contact.business_phone_number ? (
        <PhoneDisplay title={t("phone")} phoneNumber={contact.business_phone_number} />
      ) : null}
      <EmploymentLink contact={contact} />
      {contact.eeo_report_asset ? (
        <EEOReportAsset eeoReportAsset={formatImage(contact.eeo_report_asset?.id, env.IMAGE_HOST)} />
      ) : null}
    </section>
  );
};

const Intro = ({ contact }: { contact?: ContactFragment | null }) => {
  if (!contact) {
    return null;
  }
  if (contact.page_intro) {
    return (
      <section title="Intro">
        <p>{contact.page_intro}</p>
      </section>
    );
  }
  return null;
};

export const Contact = inject("store")(({ store, block }: ContactProps & PageBlockInterface) => {
  if (!store) {
    return null;
  }

  const { site } = store;
  const name = site.sections.general?.name || "";
  useEffect(() => store.storeBlock(block));

  return (
    <Container className="contact-container">
      <Intro contact={site.sections.contact} />
      <ContestPromotionsSupport
        contestPromotionSwitch={site.sections?.contact?.enable_contest_promotions}
        name={name}
        iheartHelpSiteContest={store.env.IHEART_HELP_SITE_CONTEST}
      />
      <Social socialSwitch={site.sections.contact?.show_social_switch} social={site.sections.social} />
      <Phones contact={site.sections.contact} />
      <Advertise name={name} />
      <Support showSupport={site.sections.partners?.iheartradio_switch} iheartHelpSite={store.env.IHEART_HELP_SITE} />
      <PrizePickup contact={site.sections.contact} name={name} />
      <EmploymentInfo contact={site.sections.contact} name={name} env={store.env} />
      <ContactForm />
    </Container>
  );
});

export default Contact;
